import { memo, useCallback } from 'react';
import { Badge, Button, CartContainer, Tooltip } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { ArrowRight } from 'lucide-react';
import IntegrationProviderLogo from './IntegrationProviderLogo.jsx';
import StatusPill from './StatusPill.jsx';
import { catalogCategoryLabel } from '../utils';
import { STATUS_BUCKET } from '../constants';

// Native Enter/Space on the CTA <button> fires a keydown before the browser
// synthesizes the click, so it reaches CartContainer's own onKeyDown first —
// stopping only the click isn't enough. Same convention as EntityListCard.jsx.
const stopPropagation = (e) => e.stopPropagation();

/**
 * Connected Integration Card (CT-4161): the repeating card inside each category
 * group on Already Integrated. Health + usage summary and the entry point to
 * Provider Detail. CT-4199 later layers the status tooltip on this same card —
 * do not create a second card.
 *
 * `view` is produced by `mapApiConnectedToUi`. The whole card body navigates via
 * `onOpen(slug)`; the inline CTA stops propagation (click + keydown) so it
 * never navigates.
 */
function ConnectedIntegrationCard({ view, onOpen }) {
  const { t } = useTranslation('admin-studio');

  const usageParts = [
    t('integrations.card.instances', { count: view.instancesCount }),
    t('integrations.card.callsPerDay', { count: view.callsPerDay }),
  ];
  if (view.usedInWorkflows > 0) {
    usageParts.push(t('integrations.card.usedInWorkflows', { count: view.usedInWorkflows }));
  }

  const handleOpen = useCallback(() => onOpen?.(view.slug), [onOpen, view.slug]);
  const handleCtaClick = useCallback(
    (e) => {
      e.stopPropagation();
      onOpen?.(view.slug);
    },
    [onOpen, view.slug],
  );

  return (
    <CartContainer
      size="S"
      aria-label={t('integrations.card.openAria', { name: view.name })}
      onClick={handleOpen}
      className="gap-3"
    >
      <div className="flex items-start gap-3">
        <IntegrationProviderLogo slug={view.slug} name={view.name} size="medium" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-content">{view.name}</p>
          <p className={`truncate text-xs ${view.categoryColorClass || 'text-content-muted'}`}>
            {catalogCategoryLabel(view.category, t)}
          </p>
        </div>
        {view.statusBucket === STATUS_BUCKET.WORKING ? (
          <StatusPill status={view.status} activeVariant={view.callsPerDay > 0} />
        ) : (
          <Tooltip
            content={view.statusSummary ?? t(view.statusSummaryKey)}
            arrow={false}
            align="start"
          >
            <span className="inline-flex">
              <StatusPill status={view.status} />
            </span>
          </Tooltip>
        )}
      </div>

      {view.activeCapabilities.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {view.activeCapabilities.map((type) => (
            <Badge key={type} variant="neutral" size="small" radius="full">
              {t(`integrations.card.capability.${type}`)}
            </Badge>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <p className="truncate text-xs text-content-muted">{usageParts.join(' · ')}</p>
        {view.ctaLabelKey && (
          <Button
            variant="tertiary"
            size="sm"
            rightIcon={ArrowRight}
            onClick={handleCtaClick}
            onKeyDown={stopPropagation}
          >
            {t(view.ctaLabelKey)}
          </Button>
        )}
      </div>
    </CartContainer>
  );
}

export default memo(ConnectedIntegrationCard);
